import camelCase from 'lodash/camelCase';

import type { SSOProvider } from './sso';
import type { FileDownloadRequest } from './types/flowTypes';

export const APIEndpoints = {
  // Onboarding
  generateEmailVerificationCode: '/v3/users/verificationCode',
  verifyEmailVerificationCode: '/v3/users/verify',
  updateUserDetails: '/v3/users/profile',
  verifyGoogleSSO: '/v3/users/identity/gsuite/oneTap',
  verifyEmailWithSSO: (
    name: SSOProvider,
    authorizationFlow: 'login' | 'signup',
    isEnforcedAuth: boolean
  ) =>
    `/v3/users/identity/${camelCase(name)}/sso${
      isEnforcedAuth ? '/enforced' : ''
    }/${authorizationFlow}`,

  accountSetup: '/v3/feed/jobs/accountSetup',
  onboardingJobsList: '/v3/feed/flows/onboardingJob',
  onboardingJobsStatus: (ids: string[]) =>
    `/v3/monitoring/job/status?ids=${ids.join(',')}`,
  updateUserTimeZone: `/v2/user/settings/updateaccount`,

  // Workspaces
  listWorkspaces: '/v3/users/workspace',
  createWorkspace: '/v3/assembly/create',
  acceptWorkspaceInvite: '/v3/users/onboard/invite',
  joinWorkspace: '/v3/users/assembly/join',
  loginToWorkspace: '/v3/users/session/login',
  logout: '/v3/users/session/clearCookies',

  userDetails: '/v3/assembly/members/info',
  registerMagicLinkUser: '/v3/users/register/magiclink',
  registerMarketingEvents: '/v2/metrics/events/record',
  assemblyInfoFromSlugShortCode: (slugShortCode: string) =>
    `/v3/assembly/workspace/${slugShortCode}/info`,

  signOut: '/v3/users/session/logout',

  // templates
  listTemplates: '/v3/feed/external/flows/template',
  listCategories: '/v3/feed/external/flows/template/type/filters',

  // plans
  getPlanFeatures: '/v3/billing/planFeatures',

  // DORA
  askDora: '/v1/qna/ask',
  flowSummary: '/v1/summary/generateSummary',
  collectFeedback: '/v1/qna/feedback',
  getDoraStatus: '/v3/search/isDoraReady',
  maxFlowSummaryTimeInterval: '/v1/summary/calculateTokenLimitDate',
  searchSuggestedQuestions: '/v3/search/searchQuestions',
  suggestedQuestions: '/v1/qna/suggestedQuestions',

  // assembly post oEmbeds
  assemblyFlowPost: ({
    flowId,
    responseId,
  }: {
    flowId: string;
    responseId: string;
  }) => `/v3/feed/flows/${flowId}/response/${responseId}`,
  assemblyRecognitionPost: (responseId: string) =>
    `/v3/feed/posts/${responseId}`,
  assemblyPostReplies: ({
    flowId,
    responseId,
  }: {
    flowId: string;
    responseId: string;
  }) =>
    `/v3/feed/posts/${responseId}/replySummary?type=${
      flowId === 'recognition' ? 'POST' : 'RESPONSE'
    }`,
  assemblyPostReaction: ({
    flowId,
    responseId,
    action,
  }: {
    flowId: string;
    responseId: string;
    action: string;
  }) =>
    flowId === 'recognition'
      ? `/v3/feed/posts/${responseId}/react/${action}`
      : `/v3/feed/flows/${flowId}/response/${responseId}/react/${action}`,

  searchIndex: '/v3/search/searchindex',
  getFileDownloadURL: (request: FileDownloadRequest) => {
    const { flowId, responseId, blockId, fileName } = request;
    return `v3/feed/flows/${flowId}/response/${responseId}/block/${blockId}/file/${fileName}`;
  },

  // Rewards endpoints
  rewardsDetails: ({ category }: { category: string }) =>
    `/v2/rewards/getreward/tango?category=${encodeURI(
      category
    )}&sort=popular:desc`,
  redeem: '/v3/billing/checkRedeem',
  getExchangeRates: '/v2/rewards/exchangeRates',
  rewardCashOut: '/v2/rewards/cashout',
  customRewardCashOut: '/v2/rewards/cashout/custom',
  countries: '/v3/search/tangoFilter',
  swagCategories: 'v3/rewards/integrations/AXOMO/rewardCategory',

  // Important section
  getImportantActivities: '/v3/notification/cards',
  dismissImportantActivity: (cardId: string) =>
    `/v3/notification/cards/${cardId}`,
  getActivityByCardId: (cardId: string) => `/v3/notification/cards/${cardId}`,
  getCarouselListByCardId: (cardId: string) =>
    `v3/feed/carousel/cards/${cardId}`,
  getFlowsToAnswerNow: '/v3/feed/flows/list/todo',
  getFlowsToAnswerAnytime: '/v3/feed/flows/list/shortcut',
  getFlowsToDoCount: '/v3/feed/flows/count/todo',
  addUserActivity: '/v3/search/storeActivity',
  getUserFeed: '/v3/search/getUserFeed',
  updatePostNotification: (postId: string) =>
    `/v3/feed/posts/${postId}/notificationPreference`,
  updateFlowResponseNotification: (flowId: string, responseId: string) =>
    `/v3/feed/flows/${flowId}/response/${responseId}/notificationPreference`,
  updateCarouselPostByCardId: (cardId: string) =>
    `v3/notification/cards/${cardId}/carousel`,

  // Notebook endpoints
  updateNotebookTask: (view: string) => `/v3/feed/notebook/task/${view}`,

  // Post/Reply endpoints
  archiveComment: '/v3/feed/comments/archive',
  archiveFlowPost: (flowId: string, responseId: string) =>
    `/v3/feed/flows/${flowId}/response/${responseId}`,
  archivePost: '/v3/feed/posts/archive',

  getCanPostFlows: '/v3/feed/flows/activityFeed',

  // Connections endpoints
  connectionPool: '/v3/assembly/integration/connection/pool',
  disconnectApp: (connectionId: string) =>
    `/v3/assembly/integration/connection/${connectionId}/disconnect`,
  linkToken: '/v3/assembly/integration/merge/member/linkToken',
  listConnections: '/v3/assembly/integration/connection/list',

  // Collections endpoints
  deleteCollection: (collectionId: string) =>
    `/v3/assembly/members/collection/${collectionId}/delete`,
  listCollections: '/v3/assembly/members/collection/list',
  listCollectionItems: (collectionId: string) =>
    `/v3/assembly/members/collection/${collectionId}/list/items`,
  updateCollection: '/v3/assembly/members/collection',
  updateCollectionItems: ({
    collectionId,
    action,
  }: {
    collectionId: string;
    action: string;
  }) => `/v3/assembly/members/collection/${collectionId}/items/${action}`,
  getCollectionShareCriteria: (collectionId: string) =>
    `/v3/assembly/members/collection/${collectionId}/sharing`,
  previewCollectionShareCriteria: `v3/assembly/members/criteria/preview`,
  previewMembersForCollectionShareCriteria: (previewId: string) =>
    `v3/assembly/members/criteria/preview/${previewId}`,
  reorderCollections: (collectionId: string) =>
    `/v3/assembly/members/collection/${collectionId}/reorder`,
  reorderCollectionItems: (collectionId: string, itemId: string) =>
    `/v3/assembly/members/collection/${collectionId}/list/items/${itemId}`,

  // Flow endpoints
  editPostInteractionSettings: (flowId: string, responseId: string) =>
    `/v3/feed/flows/${flowId}/response/${responseId}/settings`,
  saveAnnouncement: '/v3/feed/announcements',
  getAnnouncements: '/v3/feed/announcements',
  getEntityPermissions: '/v3/feed/entity/permissions',
  endAnnouncement: (announcementId: string) =>
    `/v3/feed/announcements/${announcementId}/end`,
  seenAnnouncement: (announcementId: string) =>
    `/v3/feed/announcements/${announcementId}/seen`,
  getAnnouncementInsightsViewers: (announcementId: string) =>
    `/v3/feed/announcements/${announcementId}/insights`,
  getAnnouncementById: (announcementId: string) =>
    `/v3/feed/announcements/${announcementId}`,
  archiveFlow: (flowId: string) => `/v3/feed/flows/${flowId}/archive`,
  unArchiveFlow: (flowId: string) => `/v3/feed/flows/${flowId}/unarchive`,

  getMembers: '/v3/assembly/members',
  getDepartments: '/v3/assembly/departments',
  postProfileData: '/v3/assembly/profileData',
  postedBy: '/v3/feed/filter/postedBy',
  flowBlocks: '/v3/feed/filter/blocks',

  //Secondary filters
  getSecondaryFilters: '/v3/search/secondaryFilter',

  // Nav
  getNavItems: '/v3/assembly/members/nav/items',
  pinItemToNav: '/v3/assembly/members/nav/pinItem',
  updateNavItems: (itemId: string) =>
    `/v3/assembly/members/nav/pinItem/${itemId}`,
  updatePinnedCollectionsToggleState: `/v3/assembly/members/nav/pinnedItems`,
} as const;
